"use client"

import { useState, useEffect, useRef } from "react"
import { useRequireAuth } from "@/hooks/useAuth"
import { useRouter } from "next/navigation"
import ProtectedLayout from "@/components/protected-layout"
import UpcomingEvents from "@/components/admin/upcoming-events"
import { Calendar, ListChecks, NotebookPen, Plus, ArrowRight, ShoppingBasket, User, Scissors, QrCode, CheckSquare } from "lucide-react"
import Link from "next/link"
import RecentNotesGrid from "@/components/admin/RecentNotesGrid"
import TasksTable from "@/components/admin/tasks-table"
import { Note, Task } from "@/types/admin"
import { MyAssignmentsModal } from "@/components/assignments/MyAssignmentsModal"

interface TasksStatsClient {
  id: string;
  name: string;
  imageUrl?: string | null;
  color?: string | null;
  count: number;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    dueDate?: string | null;
  }>;
}
import { NotesChart } from "@/components/admin/NotesChart"
import { TasksChart } from "@/components/admin/TasksChart"
import { TasksBarChart } from "@/components/admin/TasksBarChart"
import RecentActiveUsers from "@/components/RecentActiveUsers"

interface CalendarEvent {
  id: string
  title: string
  description: string | null
  startTime: string
  endTime: string | null
  allDay: boolean
  type: string
  color: string
  client: {
    id: string
    name: string
    imageUrl: string | null
  } | null
  task: {
    id: string
    title: string
    status: string
    priority: string
  } | null
  createdBy: {
    username: string
    avatarUrl: string | null
  }
}

interface RecentUser {
  id: string
  username: string
  avatarUrl?: string | null
  isOnline: boolean
  lastOnline?: string | null
}

interface DetailedStats {
  notes: {
    total: number
    unreadSinceYesterday: number
  }
  tasks: {
    total: number
    overdue: number
    dueToday: number
  }
  orders: {
    total: number
    deliveryToday: number
    deliveryTomorrow: number
  }
  events: {
    total: number
    today: number
    tomorrow: number
  }
}


// CountUp animation component

function CountUp({ end, duration = 1500, className = "" }: { end: number, duration?: number, className?: string }) {
  const [count, setCount] = useState(0);
  const countRef = useRef(0); // Przechowuje aktualną wartość animacji
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Resetuj animację przy zmianie 'end'
    const startValue = 0; // Zawsze startujemy od 0 dla efektu wejścia
    startTimeRef.current = null;

    // Jeśli end jest 0, po prostu ustaw 0 i nie animuj
    if (end === 0) {
      setCount(0);
      return;
    }

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = timestamp - startTimeRef.current;
      const percentage = Math.min(progress / duration, 1);

      // Easing function (easeOutExpo) dla płynniejszego efektu
      const easeOut = (x: number): number => {
        return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
      };

      const currentCount = Math.floor(startValue + (end - startValue) * easeOut(percentage));

      setCount(currentCount);
      countRef.current = currentCount;

      if (percentage < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end); // Upewnij się, że kończymy na dokładnej liczbie
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [end, duration]);

  return <span className={className}>{count}</span>;
}



export default function Home() {
  const { data: session } = useRequireAuth()
  const router = useRouter()
  const [isMyAssignmentsOpen, setIsMyAssignmentsOpen] = useState(false)
  const [stats, setStats] = useState<DetailedStats>({
    notes: { total: 0, unreadSinceYesterday: 0 },
    tasks: { total: 0, overdue: 0, dueToday: 0 },
    orders: { total: 0, deliveryToday: 0, deliveryTomorrow: 0 },
    events: { total: 0, today: 0, tomorrow: 0 }
  })
  const [recentTasks, setRecentTasks] = useState<Task[]>([])
  const [tasksStatsClients, setTasksStatsClients] = useState<TasksStatsClient[]>([])
  const [recentNotes, setRecentNotes] = useState<Note[]>([])
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarEvent[]>([])
  const [notesChartData, setNotesChartData] = useState<{ date: string; created: number; read: number }[]>([])
  const [loading, setLoading] = useState(true)
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([])
  const [todayAssignmentsUsers, setTodayAssignmentsUsers] = useState<any[]>([])

  // Funkcja do kapitalizacji pierwszej litery
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getTodayYYYYMMDD = () => {
    const d = new Date()
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      // Wykryj mobile device
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
      const quickTimeout = isMobile ? 5000 : 10000
      const normalTimeout = isMobile ? 8000 : 15000

      // Najpierw ładuj krytyczne dane (stats) z krótszym timeoutem
      const statsController = new AbortController()
      const statsTimeout = setTimeout(() => statsController.abort(), quickTimeout)

      try {
        const statsRes = await fetch('/api/admin/stats/detailed', {
          signal: statsController.signal
        })
        clearTimeout(statsTimeout)

        if (statsRes.ok) {
          const statsData = await statsRes.json()
          setStats(statsData)
        }
      } catch (error) {
        clearTimeout(statsTimeout)
        console.error("Error fetching stats:", error)
      }

      setLoading(false) // Pokaż UI szybciej nawet jeśli stats się nie załadowały

      // Potem ładuj resztę danych w tle z timeoutami
      const fetchWithTimeout = async (url: string, timeout = normalTimeout) => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        try {
          const response = await fetch(url, { signal: controller.signal })
          clearTimeout(timeoutId)
          return response
        } catch (error) {
          clearTimeout(timeoutId)
          throw error
        }
      }

      const todayStr = getTodayYYYYMMDD()

      const [tasksStatsRes, notesRes, eventsRes, chartRes, usersRes, assignmentsRes] = await Promise.allSettled([
        fetchWithTimeout('/api/admin/tasks/stats'),
        fetchWithTimeout('/api/admin/notes/recent'),
        fetchWithTimeout('/api/calendar/upcoming'),
        fetchWithTimeout('/api/admin/stats/notes-chart'),
        fetchWithTimeout('/api/admin/users'),
        fetchWithTimeout(`/api/admin/assignments?day=${encodeURIComponent(todayStr)}`)
      ])

      if (tasksStatsRes.status === 'fulfilled' && tasksStatsRes.value.ok) {
        const statsClientsData = await tasksStatsRes.value.json()
        setTasksStatsClients(statsClientsData)
      }

      if (notesRes.status === 'fulfilled' && notesRes.value.ok) {
        const notesData = await notesRes.value.json()
        setRecentNotes(notesData)
      }

      if (eventsRes.status === 'fulfilled' && eventsRes.value.ok) {
        const eventsData = await eventsRes.value.json()
        setUpcomingEvents(eventsData)
      }

      if (chartRes.status === 'fulfilled' && chartRes.value.ok) {
        const chartData = await chartRes.value.json()
        setNotesChartData(chartData)
      }

      if (usersRes.status === 'fulfilled' && usersRes.value.ok) {
        const usersData = await usersRes.value.json()
        const sortedUsers = usersData.sort((a: RecentUser, b: RecentUser) => {
          if (a.isOnline && !b.isOnline) return -1
          if (!a.isOnline && b.isOnline) return 1

          const aTime = a.lastOnline ? new Date(a.lastOnline).getTime() : 0
          const bTime = b.lastOnline ? new Date(b.lastOnline).getTime() : 0
          return bTime - aTime
        })
        setRecentUsers(sortedUsers)
      }

      if (assignmentsRes.status === 'fulfilled' && assignmentsRes.value.ok) {
        const assignmentsData = await assignmentsRes.value.json()
        setTodayAssignmentsUsers(assignmentsData.users || [])
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      setLoading(false)
    }
  }


  const [greeting, setGreeting] = useState({ title: "Cześć", subtitle: "" })
  const [currentDate, setCurrentDate] = useState("")

  useEffect(() => {
    const now = new Date()
    const hour = now.getHours()
    const day = now.getDay() // 0 = Niedziela, 6 = Sobota

    // --- Baza tekstów ---

    // 1. Powitania główne (Losowane)
    const mainGreetings = ["Cześć", "Hej", "Witaj", "Dzień dobry", "Siema"]

    // 2. Teksty na weekend (Sobota, Niedziela)
    const weekendTexts = [
      "Odpoczywaj, zasłużyłeś!",
      "Mamy weekend – ładuj baterie.",
      "Praca nie zając, dzisiaj relaks.",
      "Mamy nadzieję, że spędzasz ten czas z dala od komputera!"
    ]

    // 3. Teksty poza godzinami pracy w tygodniu (<8:00 lub >16:00)
    const beforeWorkTexts = [
      "Jeszcze wcześnie! Złap kawę przed startem.",
      "Dzień dobry, gotowy na podbój świata?",
      "Spokojny start dnia to podstawa."
    ]
    const afterWorkTexts = [
      "Już po 16:00. Czas na odpoczynek!",
      "Biuro zamknięte (teoretycznie). Miłego popołudnia!",
      "Dobra robota dzisiaj. Do jutra!",
      "Zamykamy laptopy, odpalamy życie prywatne."
    ]

    // 4. Teksty godzinowe (Praca 8-16)
    // Klucz to godzina. Możesz tu dodać dowolną liczbę tekstów dla konkretnej godziny.
    const workHoursTexts: Record<number, string[]> = {
      8: [ // Godzina 8:00 - 8:59
        "Zaczynamy! Kawa w dłoń i lecimy.",
        "Nowy dzień, nowe wyzwania. Powodzenia!",
        "Startujemy. Szykuje się produktywny dzień.",
        "Ósma rano – idealna pora na przegląd zadań.",
        "Witaj w pracy. Oby poszło gładko!"
      ],
      9: [ // Godzina 9:00 - 9:59
        "Już dziewiąta. Pełne skupienie!",
        "Rozkręcamy się. Jak tam lista zadań?",
        "Druga kawa? Czemu nie.",
        "Godzina 9:00 – najwyższa produktywność."
      ],
      10: ["Działamy, działamy!", "Środek poranka, trzymaj tempo."],
      11: ["Już prawie południe.", "Nie zapomnij o krótkiej przerwie dla oczu."],
      12: ["Samo południe. Czas na lunch?", "Połowa dnia pracy za nami."],
      13: ["Byle do szesnastej!", "Energia spada? Czas na mały spacer."],
      14: ["Jeszcze dwie godzinki.", "Ostatnia prosta na dziś."],
      15: ["Już za godzinę fajrant!", "Kończymy powoli rozpoczęte tematy.", "Ostatnia godzina – domykamy taski."],
      // Fallback dla innych godzin w razie zmiany zakresu pracy
    }

    // --- Algorytm wyboru tekstu ---

    const getRandom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)]

    let selectedSubtitle = ""

    // Czy weekend?
    if (day === 0 || day === 6) {
      selectedSubtitle = getRandom(weekendTexts)
    } else {
      // Dzień roboczy
      if (hour < 8) {
        selectedSubtitle = getRandom(beforeWorkTexts)
      } else if (hour >= 16) {
        selectedSubtitle = getRandom(afterWorkTexts)
      } else {
        // Godziny pracy (8-15)
        // Sprawdzamy czy mamy specjalne teksty dla tej konkretnej godziny
        const specificTexts = workHoursTexts[hour]
        if (specificTexts) {
          selectedSubtitle = getRandom(specificTexts)
        } else {
          selectedSubtitle = "Miłego dnia w pracy!"
        }
      }
    }

    setGreeting({
      title: getRandom(mainGreetings),
      subtitle: selectedSubtitle
    })

    // Data
    setCurrentDate(now.toLocaleDateString('pl-PL', {
      weekday: 'long', day: 'numeric', month: 'long'
    }))

  }, [])

  if (loading) {
    return (
      <ProtectedLayout>
        <div className="space-y-6 sm:space-y-8">
          <div className="animate-pulse space-y-4 sm:space-y-6">
            {/* Welcome Header Skeleton */}

            {/* Stats Grid Skeleton */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-zinc-800 dark:to-zinc-900 rounded-xl h-40"></div>
              ))}
            </div>

            {/* Tables Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8">
              <div className="lg:col-span-3 bg-white dark:bg-zinc-900 rounded-xl p-6 border border-gray-200 dark:border-zinc-800">
                <div className="h-64 bg-gray-100 dark:bg-zinc-800 rounded-lg"></div>
              </div>
              <div className="lg:col-span-2 bg-white dark:bg-zinc-900 rounded-xl p-6 border border-gray-200 dark:border-zinc-800">
                <div className="h-64 bg-gray-100 dark:bg-zinc-800 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </ProtectedLayout>
    )
  }



  return (
    <ProtectedLayout>
      <div className="space-y-6 sm:space-y-8">
        {/* Przycisk dodaj notatkę - tylko na mobile */}
        <div className="lg:hidden">
          <Link
            href="/notes/new"
            className="flex items-center w-full bg-theme rounded-2xl overflow-hidden shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200"
          >
            {/* Lewa strona - kwadratowy ciemny box z ikoną */}
            <div className="flex items-center justify-center w-24 h-24 bg-black/20 flex-shrink-0">
              <NotebookPen className="w-10 h-10 text-white" />
            </div>

            {/* Prawa strona - duży tekst */}
            <div className="flex-1 flex items-center  py-4 px-6">
              <span className="text-2xl font-bold text-white">
                Dodaj notatkę
              </span>
            </div>
          </Link>
        </div>

        <div className="lg:hidden">
          <Link
            href="/magazyn/zuzycie/new"
            className="flex items-center w-full bg-theme rounded-2xl overflow-hidden shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200"
          >
            {/* Lewa strona - kwadratowy ciemny box z ikoną */}
            <div className="flex items-center justify-center w-24 h-24 bg-black/20 flex-shrink-0">
              <Scissors className="w-10 h-10 text-white" />
            </div>

            {/* Prawa strona - duży tekst */}
            <div className="flex-1 flex items-center  py-4 px-6">
              <span className="text-2xl font-bold text-white">
                Zużycie (Manualne)
              </span>
            </div>
          </Link>
        </div>

        <div className="lg:hidden mt-2">
          <button
            onClick={() => setIsMyAssignmentsOpen(true)}
            className="flex items-center w-full bg-gradient-to-r from-emerald-700 to-teal-600 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200 text-left"
          >
            {/* Lewa strona - kwadratowy ciemny box z ikoną */}
            <div className="flex items-center justify-center w-24 h-24 bg-black/20 flex-shrink-0">
              <CheckSquare className="w-10 h-10 text-white" />
            </div>

            {/* Prawa strona - duży tekst */}
            <div className="flex-1 flex items-center py-4 px-6">
              <span className="text-2xl font-bold text-white">
                Moje Przydziały
              </span>
            </div>
          </button>
        </div>

        <MyAssignmentsModal
          isOpen={isMyAssignmentsOpen}
          onClose={() => setIsMyAssignmentsOpen(false)}
        />




        <div className="flex items-end justify-between relative  group">

          <div className="relative z-10">


            {/* Powitanie z avatarem */}
            <div className="flex items-center gap-4">
              {/* Avatar z kropką online */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center   bg-white dark:bg-zinc-800">
                  {session?.user?.avatarUrl ? (
                    <img
                      src={session.user.avatarUrl}
                      alt={session.user.username || "User"}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-zinc-800 dark:to-zinc-700 flex items-center justify-center">
                      <User className="h-8 w-8 text-gray-600 dark:text-zinc-400" />
                    </div>
                  )}
                </div>
                {/* Zielona kropka online */}
                <div className="absolute bottom-[-5] right-[-5] w-5 h-5 bg-green-500 rounded-full border-2 border-white dark:border-zinc-900"></div>
              </div>

              {/* Tekst powitania */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-500 dark:text-zinc-400 uppercase tracking-wider">
                    {currentDate}
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-zinc-100 tracking-tight">
                  {greeting.title}, <span className="bg-clip-text">
                    {session?.user?.username ? capitalizeFirstLetter(session.user.username) : "Użytkowniku"}
                  </span> 👋
                </h1>
                <p className="mt-2 text-base text-gray-600 dark:text-zinc-400 max-w-2xl">
                  {greeting.subtitle}
                </p>
              </div>
            </div>
          </div>

        </div>





        {/* Ostatnio aktywni użytkownicy - tylko mobile */}
        <div className="lg:hidden">
          <RecentActiveUsers
            users={recentUsers}
            currentUserId={session?.user?.id || (session?.user as any)?.sub}
            showTitle={true}
            showScrollHint={true}
            skeletonCount={6}
          />
        </div>



        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-6">
          <Link
            href="/notes"
            className="group relative overflow-hidden rounded-2xl p-4 sm:p-6 transition-all duration-200 active:scale-95 bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700 cursor-pointer shadow-lg shadow-emerald-900/10 hover:shadow-xl hover:shadow-emerald-900/20 hover:-translate-y-0.5 ring-1 ring-white/20"
          >
            {/* subtle texture + shine */}
            <div className="pointer-events-none absolute inset-0 opacity-70">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.35),transparent_45%)]" />
              <div className="absolute -left-24 -top-24 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-black/10 blur-2xl" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-white/20 ring-1 ring-white/20 shadow-sm group-hover:bg-white/25 transition-colors">
                  <NotebookPen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                {stats.notes.unreadSinceYesterday > 0 && <div className="h-2 w-2 rounded-full bg-white/40 animate-pulse" />}
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                <CountUp end={stats.notes.total} />
              </div>
              <p className="text-sm font-semibold text-white/95 mb-1">
                Notatki
              </p>
              <p className="text-xs text-white/70">
                <CountUp end={stats.notes.unreadSinceYesterday} /> nieprzeczytanych od wczoraj
              </p>
            </div>
          </Link>

          <Link
            href="/admin/tasks"
            className="group relative overflow-hidden rounded-2xl p-4 sm:p-6 transition-all duration-200 active:scale-95 bg-gradient-to-br from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 cursor-pointer shadow-lg shadow-amber-900/10 hover:shadow-xl hover:shadow-amber-900/20 hover:-translate-y-0.5 ring-1 ring-white/20"
          >
            <div className="pointer-events-none absolute inset-0 opacity-70">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.35),transparent_45%)]" />
              <div className="absolute -left-24 -top-24 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-black/10 blur-2xl" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-white/20 ring-1 ring-white/20 shadow-sm group-hover:bg-white/25 transition-colors">
                  <ListChecks className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                {stats.tasks.overdue > 0 && <div className="h-2 w-2 rounded-full bg-white/40 animate-pulse" />}
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                <CountUp end={stats.tasks.total} />
              </div>
              <p className="text-sm font-semibold text-white/95 mb-1">
                Zadania
              </p>
              <p className="text-xs text-white/70">
                {stats.tasks.overdue > 0 ? <><CountUp end={stats.tasks.overdue} /> po terminie, <CountUp end={stats.tasks.dueToday} /> dzisiaj</> : <><CountUp end={stats.tasks.dueToday} /> dzisiaj</>}
              </p>
            </div>
          </Link>

          <Link
            href="/orders"
            className="group relative overflow-hidden rounded-2xl p-4 sm:p-6 transition-all duration-200 active:scale-95 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 cursor-pointer shadow-lg shadow-blue-900/10 hover:shadow-xl hover:shadow-blue-900/20 hover:-translate-y-0.5 ring-1 ring-white/20"
          >
            <div className="pointer-events-none absolute inset-0 opacity-70">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.35),transparent_45%)]" />
              <div className="absolute -left-24 -top-24 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-black/10 blur-2xl" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-white/20 ring-1 ring-white/20 shadow-sm group-hover:bg-white/25 transition-colors">
                  <ShoppingBasket className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                {stats.orders.deliveryToday > 0 && <div className="h-2 w-2 rounded-full bg-white/40 animate-pulse" />}
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                <CountUp end={stats.orders.total} />
              </div>
              <p className="text-sm font-semibold text-white/95 mb-1">
                Zamówienia
              </p>
              <p className="text-xs text-white/70">
                <CountUp end={stats.orders.deliveryToday} /> dzisiaj, <CountUp end={stats.orders.deliveryTomorrow} /> jutro
              </p>
            </div>
          </Link>

          <Link
            href="/calendar"
            className="group relative overflow-hidden rounded-2xl p-4 sm:p-6 transition-all duration-200 active:scale-95 bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700 cursor-pointer shadow-lg shadow-purple-900/10 hover:shadow-xl hover:shadow-purple-900/20 hover:-translate-y-0.5 ring-1 ring-white/20"
          >
            <div className="pointer-events-none absolute inset-0 opacity-70">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.35),transparent_45%)]" />
              <div className="absolute -left-24 -top-24 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-black/10 blur-2xl" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-white/20 ring-1 ring-white/20 shadow-sm group-hover:bg-white/25 transition-colors">
                  <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                <CountUp end={stats.events.total} />
              </div>
              <p className="text-sm font-semibold text-white/95 mb-1">
                Wydarzenia
              </p>
              <p className="text-xs text-white/70">
                <CountUp end={stats.events.today} /> dzisiaj, <CountUp end={stats.events.tomorrow} /> jutro
              </p>
            </div>
          </Link>

          {/* Ostatnio aktywni użytkownicy - tylko desktop (2 kolumny) */}
          <div className="hidden lg:block lg:col-span-2">
            <RecentActiveUsers
              users={recentUsers}
              currentUserId={session?.user?.id || (session?.user as any)?.sub}
              showTitle={true}
              showScrollHint={false}
              skeletonCount={8}
            />
          </div>
        </div>

        {/* Recent Notes (Left - 2 cols) & Today's People Assignments (Right - 3 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8">
          {/* Recent Notes - 2 kolumny po lewej */}
          <div className="lg:col-span-2 bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-gray-200 dark:border-zinc-800 overflow-hidden transition-colors flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-zinc-800">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-zinc-100 flex items-center">
                  <NotebookPen className="w-5 h-5 mr-2 text-emerald-500" />
                  Ostatnie notatki
                </h2>
                <Link
                  href="/notes"
                  className="inline-flex items-center text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 px-3 py-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-all duration-200"
                >
                  Zobacz wszystkie
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Najnowsze notatki dodane przez zespół.</p>
            </div>

            <div className="p-4 sm:p-6 flex-1">
              <RecentNotesGrid notes={recentNotes} onNoteClick={(noteId) => window.location.href = `/notes`} />
            </div>
          </div>

          {/* Dzisiejsze przydziały - 3 kolumny po prawej */}
          <div className="lg:col-span-3 bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-gray-200 dark:border-zinc-800 overflow-hidden transition-colors flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-zinc-800">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-zinc-100 flex items-center">
                  <ListChecks className="w-5 h-5 mr-2 text-blue-500" />
                  Dzisiejsze przydziały
                </h2>
                <Link
                  href="/assignments"
                  className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all duration-200"
                >
                  Otwórz przydziały
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Przydziały wyznaczone dla członków zespołu na dzisiaj.</p>
            </div>

            <div className="p-4 sm:p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
              {(() => {
                const usersWithAssignments = todayAssignmentsUsers.filter((u: any) =>
                  ((u.taskAssignments?.length ?? 0) + (u.customAssignments?.length ?? 0)) > 0
                )

                if (usersWithAssignments.length === 0) {
                  return (
                    <div className="text-center py-10 text-gray-500 dark:text-zinc-400 space-y-2 flex-1 flex flex-col items-center justify-center">
                      <ListChecks className="w-10 h-10 mx-auto opacity-40 text-blue-500" />
                      <p className="text-sm font-medium">Brak przydziałów na dzisiaj</p>
                      <p className="text-xs text-gray-400 dark:text-zinc-500">Brak aktywnych zadań przypisanych zespołowi na dziś.</p>
                    </div>
                  )
                }

                return (
                  <div className="flex flex-row gap-3.5 overflow-x-auto pb-3 pt-1 scrollbar-thin flex-1 min-h-0 items-stretch">
                    {usersWithAssignments.map((u: any) => {
                      const taskItems = [
                        ...(u.customAssignments || []).map((c: any) => ({
                          id: `custom-${c.id}`,
                          title: c.title,
                          isTask: false,
                          createdBy: c.createdBy,
                          isCompleted: Boolean(c.isCompleted),
                        })),
                        ...(u.taskAssignments || []).map((a: any) => ({
                          id: a.task.id,
                          title: a.task.title,
                          projectNo: a.task.projectNumber,
                          clientName: a.task.client?.name,
                          clientImageUrl: a.task.clientImageUrl,
                          isTask: true,
                          createdBy: a.createdBy,
                          isCompleted: Boolean(a.isCompleted),
                        })),
                      ]

                      return (
                        <div
                          key={u.id}
                          className="w-[270px] sm:w-[290px] shrink-0 rounded-xl border border-gray-200 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-900/50 p-3 flex flex-col space-y-2.5 shadow-2xs h-full"
                        >
                          {/* Person Header */}
                          <div className="flex items-center justify-between gap-2 border-b border-gray-200/60 dark:border-zinc-800 pb-2 shrink-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="relative shrink-0">
                                <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center">
                                  {u.avatarUrl ? (
                                    <img src={u.avatarUrl} alt={u.username} className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-xs font-bold text-gray-700 dark:text-zinc-300">
                                      {capitalizeFirstLetter(u.username)[0]}
                                    </span>
                                  )}
                                </div>
                                {u.isOnline && (
                                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-zinc-900" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-zinc-100 truncate">
                                  {capitalizeFirstLetter(u.username)}
                                </div>
                                {u.jobTitle && (
                                  <div className="text-[11px] text-gray-500 dark:text-zinc-400 truncate">{u.jobTitle}</div>
                                )}
                              </div>
                            </div>

                            {u.dayPlan && u.dayPlan.hasPlan && (
                              <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                u.dayPlan.reason === "UNSEEN"
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                                  : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                              }`}>
                                {u.dayPlan.reason === "UNSEEN" ? "Nieodczytane" : "Odczytane"}
                              </span>
                            )}
                          </div>

                          {/* Task List: fills 100% remaining vertical space inside person column card */}
                          <div className="flex-1 flex flex-col gap-2 min-h-0 overflow-y-auto pr-1 scrollbar-thin">
                            {taskItems.map((item: any) => (
                              <div
                                key={item.id}
                                className={`p-2.5 rounded-lg border shadow-2xs space-y-1 shrink-0 transition-colors ${
                                  item.isCompleted
                                    ? "bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/40"
                                    : "bg-white dark:bg-zinc-800/90 border-gray-200 dark:border-zinc-700"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <span className="text-xs font-semibold text-gray-800 dark:text-zinc-200 line-clamp-2">
                                    {item.title}
                                  </span>
                                  {item.projectNo && (
                                    <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                                      #{item.projectNo}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500 dark:text-zinc-400 pt-0.5">
                                  {item.clientName && (
                                    <span className="inline-flex items-center gap-1">
                                      {item.clientImageUrl && (
                                        <img src={item.clientImageUrl} alt="" className="w-3.5 h-3.5 object-contain" />
                                      )}
                                      <span className="truncate max-w-[120px]">{item.clientName}</span>
                                    </span>
                                  )}
                                  {item.createdBy && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-zinc-400 bg-gray-100 dark:bg-zinc-700/60 px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-zinc-600">
                                      <span className="opacity-75">Przypisał:</span>
                                      <span className="relative flex h-3.5 w-3.5 shrink-0 overflow-hidden rounded-full border border-gray-300 dark:border-zinc-500">
                                        {item.createdBy.avatarUrl ? (
                                          <img src={item.createdBy.avatarUrl} alt={item.createdBy.username} className="object-cover w-full h-full" />
                                        ) : (
                                          <span className="flex size-full items-center justify-center rounded-full bg-primary text-primary-foreground text-[7px] font-semibold">
                                            {capitalizeFirstLetter(item.createdBy.username)[0]}
                                          </span>
                                        )}
                                      </span>
                                      <span className="font-semibold text-gray-700 dark:text-zinc-200">
                                        {capitalizeFirstLetter(item.createdBy.username)}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-gray-200 dark:border-zinc-800 overflow-hidden transition-colors">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-zinc-100 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-purple-500" />
                Nadchodzące wydarzenia
              </h2>
              <Link
                href="/calendar"
                className="inline-flex items-center text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 px-3 py-1.5 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-950/20 transition-all duration-200"
              >
                Zobacz kalendarz
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Przegląd wydarzeń na najbliższe 7 dni.</p>
          </div>

          <div className="p-4 sm:p-6">
            <UpcomingEvents events={upcomingEvents} />
          </div>
        </div>

        {/* Notes Chart */}
        {notesChartData.length > 0 && (
          <NotesChart data={notesChartData} />
        )}
      </div>
    </ProtectedLayout>
  )
}
